// The paragraph and writing steps' decisions, pure (KULA-REVISE-WORKSHOP-PLAN.md
// §4 steps 6 to 10, corrected by §0c): the facts on offer for a paragraph
// and what a wrong pick says, the check on a selection, the quick check on
// each written part (rules, not reading for meaning: that is the coach's
// job), the outline line, word targets and the keys the drafts save under.
//
// The quick check is guidance, never a mark. It looks for named case
// studies, a position, a reason, theme words, dates, an explaining phrase, the
// chosen perspective and a link back, and says what is in place and what is
// still to fix.

import type { WorkshopBlock, WorkshopChecks, WorkshopEdition, WorkshopParagraphCount } from '../types';
import { WORKSHOP_WORD_TARGETS } from '../types';
import { capitalise, copyFor, copyVars, numberWord } from './steps';

export const wordCount = (s: string): number => String(s ?? '').trim().split(/\s+/).filter(Boolean).length;
export const sentencesOf = (s: string): string[] => (String(s ?? '').match(/[^.!?]+[.!?]*/g) ?? []).map((x) => x.trim()).filter(Boolean);
export const hasKey = (low: string, keys: string[]): boolean => keys.some((k) => k && low.includes(k.toLowerCase()));

/** A written part: the introduction, a body paragraph (by its theme index), the conclusion. */
export type PartId = 'intro' | 'conclusion' | `t${number}`;
export const partIdFor = (theme: number): PartId => `t${theme}`;
export const partTheme = (id: PartId): number | null => (id.startsWith('t') ? Number(id.slice(1)) : null);

/** What a learner planned for a paragraph. */
export interface ParagraphPlan {
  /** Index into the topic sentence options, or 'own'. */
  ts: number | 'own' | null;
  own: string;
  /** Fact keys (`case:id`) selected. */
  sel: string[];
  checked: boolean;
  /** The selection passed and is the plan's evidence. */
  locked: boolean;
  /** Index into the theme's perspectives, or none. */
  persp: number | null;
  /** The learner chose to leave a perspective out. */
  noP: boolean;
  /** Supports (0) or challenges (1) the point. */
  puse: number | null;
  /** Agree (0), partly (1), disagree (2). */
  pagree: number | null;
}

export const emptyPlan = (): ParagraphPlan => ({ ts: null, own: '', sel: [], checked: false, locked: false, persp: null, noP: false, puse: null, pagree: null });

export const PERSPECTIVE_USE: Array<[string, string]> = [
  ['supports', 'It supports my point'],
  ['challenges', 'It challenges my point'],
];
export const PERSPECTIVE_AGREE = ['I agree with it', 'I partly agree with it', 'I disagree with it'];
export const PERSPECTIVE_AGREE_TEXT = ['you agree with it', 'you partly agree with it', 'you disagree with it'];

export const factKey = (caseStudy: string, id: string): string => `${caseStudy}:${id}`;

export interface FactOnOffer {
  key: string;
  caseStudy: string;
  id: string;
  text: string;
  keywords: string[];
  note?: string;
  /** Belongs in this paragraph. */
  relevant: boolean;
  /** For a distractor: where it belongs and what it shows there. */
  why?: string;
}

/**
 * The facts on offer for a paragraph on `theme`: every relevant fact from
 * each chosen case study, plus up to five distractors per case study from
 * the theme's `elsewhere` list whose own theme gets a paragraph, each with
 * the line that says where it belongs.
 */
export function factsFor(w: WorkshopBlock, chosen: string[], theme: number, paragraphThemes: number[]): FactOnOffer[] {
  const out: FactOnOffer[] = [];
  for (const k of chosen) {
    const e = w.editions[k];
    if (!e) continue;
    for (const [id, f] of Object.entries(e.facts)) if (f.theme === theme) out.push({ key: factKey(k, id), caseStudy: k, id, text: f.text, keywords: f.keywords, note: f.note, relevant: true });
    let n = 0;
    for (const id of e.elsewhere[theme] ?? []) {
      const f = e.facts[id];
      if (!f || n >= 5 || !paragraphThemes.includes(f.theme)) continue;
      n++;
      out.push({ key: factKey(k, id), caseStudy: k, id, text: f.text, keywords: f.keywords, note: f.note, relevant: false, why: `You need this fact, but not in this paragraph. It belongs in your paragraph on ${e.themes[f.theme]?.low ?? ''}, where it shows ${f.use}.` });
    }
  }
  return out;
}

export interface SelectionCheck {
  ok: boolean;
  good: string[];
  bad: string[];
  perCase: Record<string, number>;
  message: string;
}

/** Enough relevant facts, with at least one from each case study, and none that belong elsewhere. */
export function checkSelection(w: WorkshopBlock, chosen: string[], offered: FactOnOffer[], sel: string[]): SelectionCheck {
  const byKey = Object.fromEntries(offered.map((f) => [f.key, f]));
  const picked = sel.map((k) => byKey[k]).filter((f): f is FactOnOffer => !!f);
  const good = picked.filter((f) => f.relevant).map((f) => f.key);
  const bad = picked.filter((f) => !f.relevant).map((f) => f.key);
  const perCase: Record<string, number> = {};
  for (const k of chosen) perCase[k] = picked.filter((f) => f.relevant && f.caseStudy === k).length;
  const missingCase = chosen.find((k) => perCase[k] === 0);
  const ok = bad.length === 0 && good.length >= w.minFacts && !missingCase;
  let message = '';
  if (bad.length) message = bad.length === 1 ? 'One fact belongs in a different paragraph. Read where, take it out and check again.' : `${bad.length} facts belong in different paragraphs. Read where, take them out and check again.`;
  else if (good.length < w.minFacts) message = `You need at least ${w.minFacts} relevant facts. You have ${good.length} so far.`;
  else if (missingCase) message = `You have no facts from ${w.caseStudies[missingCase]?.name ?? missingCase} yet. This paragraph has to cover both ${w.unit.plural}.`;
  else message = 'Your evidence is in your plan. The facts with a dotted outline also fit this paragraph, if you want to add them.';
  return { ok, good, bad, perCase, message };
}

// ---------------------------------------------------------------------------
// The quick check on a written part.
// ---------------------------------------------------------------------------

export interface CheckItem {
  ok: boolean;
  label: string;
  message: string;
}

export interface PartContext {
  w: WorkshopBlock;
  chosen: string[];
  /** The edition the copy and checks are read from (the first chosen). */
  e: WorkshopEdition;
  checks: WorkshopChecks;
  paragraphs: WorkshopParagraphCount;
}

export interface ParagraphContext extends PartContext {
  theme: number;
  topicSentence: string;
  /** The planned facts, as offered. */
  planned: FactOnOffer[];
  /** The chosen perspective's historian match fragments and name, or null when left out. */
  perspective: { name: string; match: string[] } | null;
}

const casesNamed = (low: string, w: WorkshopBlock, chosen: string[]): Record<string, boolean> => Object.fromEntries(chosen.map((k) => [k, hasKey(low, w.caseStudies[k]?.match ?? [])]));
const caseList = (w: WorkshopBlock, chosen: string[]) => chosen.map((k) => w.caseStudies[k]?.name ?? k).join(' and ');

function lengthItem(words: number, [lo, hi]: readonly [number, number], tooLong: string): CheckItem {
  const low = lo - 20;
  const high = hi + 30;
  const aim = ` Aim for ${lo} to ${hi}.`;
  return { ok: words >= low && words <= high, label: 'Length', message: words < low ? `This is short at ${words} words.${aim}` : words > high ? `This is long at ${words} words. ${tooLong}${aim}` : `A good length at ${words} words.` };
}

function caseItem(w: WorkshopBlock, chosen: string[], low: string, checks: WorkshopChecks, verb: string): CheckItem {
  const named = casesNamed(low, w, chosen);
  const all = chosen.every((k) => named[k]);
  if (chosen.length === 2) return { ok: all, label: `Both ${w.unit.plural}`, message: all ? `You ${verb} both ${w.unit.plural}.` : checks.caseMessage ?? `Name both ${w.unit.plural}: ${caseList(w, chosen)}.` };
  return { ok: all, label: `The ${w.unit.singular}`, message: all ? `You name the ${w.unit.singular}.` : checks.caseMessage ?? `Name the ${w.unit.singular}: ${caseList(w, chosen)}.` };
}

export function checkIntro(text: string, c: PartContext): CheckItem[] {
  const low = text.toLowerCase();
  const words = wordCount(text);
  const vars = copyVars(c.w, c.chosen, c.paragraphs);
  const out: CheckItem[] = [];
  out.push(caseItem(c.w, c.chosen, low, c.checks, 'name'));
  const pos = hasKey(low, c.checks.claim);
  out.push({ ok: pos, label: 'Position', message: pos ? copyFor(c.e, 'positionYes', vars) : copyFor(c.e, 'positionNo', vars) });
  const rs = /\b(because|since|as)\b/.test(low);
  out.push({ ok: rs, label: 'Reason', message: rs ? 'You give a reason for your position.' : 'Give your main reason. Your thesis does this for you.' });
  const hits = c.checks.themeWords.filter((k) => low.includes(k.toLowerCase())).length;
  const need = c.checks.outlineHits ?? 2;
  out.push({ ok: hits >= need, label: 'Outline', message: hits >= need ? 'You tell your reader what the essay will cover.' : `Add one sentence that names the ${c.w.themeWord.plural} you will discuss. Your outline note does this for you.` });
  for (const x of c.checks.extra ?? []) {
    const ok = x.all.every((list) => hasKey(low, list));
    out.push({ ok, label: x.label, message: ok ? x.yes : x.no });
  }
  out.push(lengthItem(words, WORKSHOP_WORD_TARGETS.intro, 'Save your detail for the paragraphs.'));
  return out;
}

export function checkConclusion(text: string, c: PartContext): CheckItem[] {
  const low = text.toLowerCase();
  const sents = sentencesOf(text);
  const first = (sents[0] ?? '').toLowerCase();
  const words = wordCount(text);
  const vars = copyVars(c.w, c.chosen, c.paragraphs);
  const out: CheckItem[] = [];
  const ans = hasKey(first, c.checks.claim);
  out.push({ ok: ans, label: 'Answer', message: ans ? 'Your first sentence answers the question.' : copyFor(c.e, 'answerNo', vars) });
  out.push(caseItem(c.w, c.chosen, low, c.checks, 'judge'));
  const rs = /\b(because|since|as a result|therefore)\b/.test(low);
  out.push({ ok: rs, label: 'Reason', message: rs ? 'You give a reason for your judgement.' : 'You have given a judgement without a reason. Add your strongest reason, starting with because.' });
  const bal = /\b(although|however|it is true|yet|but|while|whereas|even though)\b/.test(low);
  out.push({ ok: bal, label: 'Balance', message: bal ? 'You weigh the other view before you decide.' : 'Say what your counter-argument gets right, then say why your judgement still holds.' });
  for (const x of c.checks.extra ?? []) {
    const ok = x.all.every((list) => hasKey(low, list));
    out.push({ ok, label: x.label, message: ok ? x.yes : x.no });
  }
  out.push(lengthItem(words, WORKSHOP_WORD_TARGETS.conclusion, 'A conclusion judges. It does not retell.'));
  return out;
}

export function checkParagraph(text: string, c: ParagraphContext): CheckItem[] {
  const low = text.toLowerCase();
  const sents = sentencesOf(text);
  const first = (sents[0] ?? '').toLowerCase();
  const last = (sents[sents.length - 1] ?? '').toLowerCase();
  const words = wordCount(text);
  const vars = copyVars(c.w, c.chosen, c.paragraphs);
  const out: CheckItem[] = [];
  const tw = c.topicSentence.toLowerCase().split(/\W+/).filter((x) => x.length > 3);
  const share = tw.filter((x) => first.includes(x)).length;
  const pointOk = tw.length > 0 && share / tw.length >= 0.5;
  out.push({ ok: pointOk, label: 'Point', message: pointOk ? 'Your first sentence makes your point.' : 'Your first sentence does not make your point yet. Open with your topic sentence.' });
  const used = c.planned.filter((f) => hasKey(low, f.keywords));
  const missing = c.planned.filter((f) => !hasKey(low, f.keywords));
  const need = Math.min(3, c.planned.length);
  const evOk = c.planned.length > 0 && used.length >= need;
  out.push({ ok: evOk, label: 'Evidence', message: `${c.planned.length ? `You used ${used.length} of the ${c.planned.length} facts in your plan.` : 'You have no facts in your plan yet.'}${missing.length ? ` Still to use: ${missing.map((f) => f.text).join('; ')}.` : ''}` });
  if (c.chosen.length === 2) {
    const per = Object.fromEntries(c.chosen.map((k) => [k, used.filter((f) => f.caseStudy === k).length]));
    const bothOk = c.chosen.every((k) => per[k] > 0);
    const none = c.chosen.find((k) => per[k] === 0);
    out.push({ ok: bothOk, label: `Both ${c.w.unit.plural}`, message: bothOk ? `You use evidence from both ${c.w.unit.plural}.` : c.chosen.every((k) => per[k] === 0) ? `Use at least one fact from each ${c.w.unit.singular}.` : `You have no evidence from ${c.w.caseStudies[none!]?.name ?? none} yet. This paragraph has to cover both ${c.w.unit.plural}.` });
  }
  const [from, to] = c.checks.yearRange ?? [1800, 1999];
  const dateOk = (text.match(/\b(1[5-9]\d\d|20\d\d)\b/g) ?? []).some((y) => Number(y) >= from && Number(y) <= to);
  out.push({ ok: dateOk, label: 'Exact dates', message: dateOk ? 'You give at least one exact date.' : 'You have no dates yet. Add at least one exact date so that your evidence is precise.' });
  const rest = sents.slice(1).join(' ').toLowerCase();
  const exOk = /\b(because|so that|which meant|this meant|this matters|as a result|therefore|since)\b/.test(rest);
  out.push({ ok: exOk, label: 'Analysis', message: exOk ? 'You explain what your facts show.' : copyFor(c.e, 'analysisNo', vars) });
  if (c.perspective) {
    const pk = [...c.perspective.match, 'historian', 'perspective'];
    const pOk = hasKey(low, pk);
    out.push({ ok: pOk, label: 'Perspective', message: pOk ? 'You bring in a perspective. Check that you also say how far you agree with it.' : `You have not used your perspective yet: ${c.perspective.name.replace(/^The /, 'the ')}. State it, then say how far you agree and why.` });
  }
  const linkOk = sents.length > 1 && (hasKey(last, c.checks.link) || /^this shows/.test(last));
  out.push({ ok: linkOk, label: 'Link', message: linkOk ? 'Your last sentence links back to the question.' : copyFor(c.e, 'linkNo', vars) });
  out.push(lengthItem(words, WORKSHOP_WORD_TARGETS.paragraph[c.paragraphs], 'Cut anything that does not prove your point.'));
  return out;
}

/** "This essay considers women's work, women's military and public roles and women's legal status." */
export function outlineText(w: WorkshopBlock, e: WorkshopEdition, chosen: string[], themes: number[]): string {
  const outs = themes.map((t) => e.themes[t]?.out ?? '').filter(Boolean);
  const list = outs.length < 2 ? outs.join('') : `${outs.slice(0, -1).join(', ')} and ${outs[outs.length - 1]}`;
  return `${copyFor(e, 'outline', copyVars(w, chosen))} ${list}.`;
}

export const partTitle = (id: PartId, order: number[]): string => (id === 'intro' ? 'Introduction' : id === 'conclusion' ? 'Conclusion' : `Paragraph ${order.indexOf(Number(id.slice(1))) + 1}`);

/** The written parts in essay order: the introduction, one per paragraph, the conclusion. */
export const essayParts = (order: number[]): PartId[] => ['intro', ...order.map(partIdFor), 'conclusion'];

/** The essay as one text: the parts that are written, in order, a blank line between. */
export const assembleEssay = (texts: string[]): string => texts.map((t) => String(t ?? '').trim()).filter(Boolean).join('\n\n');

/** "Introduction, Paragraph 1 and Paragraph 3". */
export const joinList = (a: string[]): string => (a.length < 2 ? a.join('') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);

/** What a full essay usually runs to, on the essay screen. */
export const ESSAY_WORDS_USUAL: readonly [number, number] = [800, 1000];

/** 1000 as "1 000", the way the brief writes it. */
export const thousands = (n: number): string => (n >= 1000 ? `${Math.floor(n / 1000)} ${String(n % 1000).padStart(3, '0')}` : String(n));

/** A print-styled page of the essay, for the browser's print dialog (Save as PDF). No library. */
export function essayPrintHtml(question: string, text: string): string {
  const esc = (x: string) => x.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);
  const paras = text
    .split(/\n{2,}/)
    .map((para) => para.trim())
    .filter(Boolean)
    .map((para) => `<p>${esc(para).replace(/\n/g, '<br>')}</p>`)
    .join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Essay</title><style>@page{margin:2cm}body{font:12pt/1.6 Georgia,"Times New Roman",serif;color:#111;max-width:17cm;margin:2cm auto;padding:0 1cm}h1{font:bold 13pt/1.45 Georgia,"Times New Roman",serif;margin:0 0 1.4em}p{margin:0 0 1em}</style></head><body><h1>${esc(question)}</h1>${paras}</body></html>`;
}

/** The headline of a check: how many things are still to fix. */
export const checkHeadline = (items: CheckItem[]): string => {
  const fixes = items.filter((i) => !i.ok).length;
  return fixes === 0 ? 'Everything is in place' : fixes === 1 ? 'One thing to fix' : `${capitalise(numberWord(fixes))} things to fix`;
};
